import React, { ReactNode } from 'react';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { H4 } from '@osmosis/tailwind-ui/components/Text';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Prose } from '@osmosis/tailwind-ui/components/Prose';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { textBlue, text3, text5 } from '@osmosis/tailwind-ui/styles/text';
import {
  bg0,
  bgPrimaryLightest,
  borderColorBlue600,
} from '@osmosis/tailwind-ui/styles';
import { faWandMagicSparkles } from '@fortawesome/pro-duotone-svg-icons';
import { useIntl } from 'react-intl';
import { TranscriptEntry } from '@osmosis/shared/video';

import OsmosisAIButton from '../../../ai/OsmosisAIButton';
import { chapterDomId, HighlightedChapter } from './transcriptHelpers';
import { useTranscriptKeyboardNav } from './useTranscriptKeyboardNav';

type Props = {
  chaptersWithHighlights: HighlightedChapter[];
  activeEntry: TranscriptEntry | undefined;
  isLocked: boolean;
  entriesContainerRef: React.RefObject<HTMLDivElement | null>;
  sectionRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  onTimestampClick: (entry: TranscriptEntry) => void;
  onAskAI: (text: string) => void;
  /** Floating sync pill, rendered as an overlay inside this container. */
  syncPill: ReactNode;
};

/** Chapter-grouped transcript entries: timestamp, highlighted text, Ask AI. */
export default function TranscriptEntries({
  chaptersWithHighlights,
  activeEntry,
  isLocked,
  entriesContainerRef,
  sectionRefs,
  onTimestampClick,
  onAskAI,
  syncPill,
}: Props) {
  const intl = useIntl();

  // Flat row index across chapters, so Arrow keys cross chapter boundaries.
  let runningRowCount = 0;
  const chapterRowOffset = chaptersWithHighlights.map(({ entries }) => {
    const offset = runningRowCount;
    runningRowCount += entries.length;
    return offset;
  });
  const totalRows = runningRowCount;

  const { activeIndex, setActiveIndex, setRowRef, focusRow } =
    useTranscriptKeyboardNav(totalRows);

  const handleRowKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    flatIndex: number,
    entry: TranscriptEntry,
  ) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusRow(flatIndex + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusRow(flatIndex - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusRow(0);
        break;
      case 'End':
        e.preventDefault();
        focusRow(totalRows - 1);
        break;
      case 'Enter':
      case ' ':
        if (isLocked) break;
        e.preventDefault();
        onTimestampClick(entry);
        break;
      case 'a':
      case 'A':
        e.preventDefault();
        onAskAI(entry.text);
        break;
      default:
        break;
    }
  };

  return (
    <Box
      ref={entriesContainerRef}
      data-testid="captions-entries"
      role="group"
      aria-label={intl.formatMessage({
        id: 'learn.captions_transcript.list_label',
        defaultMessage:
          'Interactive transcript. Use the up and down arrow keys to move between lines, Enter to jump the video to a line, and A to ask the AI assistant about a line.',
      })}
      className={cn('relative flex-1 px-3', isLocked && 'overflow-hidden')}
    >
      {syncPill}

      {chaptersWithHighlights.map(({ chapter, entries }, index) => {
        const chapterRange = `${chapter.startLabel}–${chapter.endLabel}`;
        return (
          <Box
            key={chapterDomId(index)}
            id={chapterDomId(index)}
            data-chapter-index={index}
            role="group"
            aria-label={
              chapter.title
                ? intl.formatMessage(
                    {
                      id: 'learn.captions_transcript.chapter_label',
                      defaultMessage: '{title} ({range})',
                    },
                    { title: chapter.title, range: chapterRange },
                  )
                : chapterRange
            }
            ref={(node: HTMLDivElement | null) => {
              sectionRefs.current[index] = node;
            }}
            className="pb-4"
          >
            {chapter.title && (
              <Box className="pt-1 pb-1">
                <H4 className="flex flex-col items-start justify-start gap-1 font-bold">
                  <span className={text5}>{chapter.title}</span>
                  <span className={cn(text3)}>{chapterRange}</span>
                </H4>
              </Box>
            )}

            {entries.map(({ entry, html }, entryIndex) => {
              const isActiveLine = entry === activeEntry;
              const flatIndex = chapterRowOffset[index] + entryIndex;
              return (
                <Flex
                  key={`${index}-${entry.start}`}
                  data-testid="captions-entry"
                  aria-current={isActiveLine ? 'true' : undefined}
                  className={cn(
                    'relative group gap-4 py-2 rounded-md pl-2 pr-2 items-center border-l-4',
                    isActiveLine
                      ? cn(borderColorBlue600, bgPrimaryLightest)
                      : 'border-transparent hover:bg-zinc-100',
                  )}
                >
                  <Button
                    variant="transparent"
                    data-testid="captions-entry-timestamp"
                    disabled={isLocked}
                    ref={setRowRef(flatIndex)}
                    tabIndex={flatIndex === activeIndex ? 0 : -1}
                    onFocus={() => setActiveIndex(flatIndex)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) =>
                      handleRowKeyDown(e, flatIndex, entry)
                    }
                    onClick={() => onTimestampClick(entry)}
                    aria-label={intl.formatMessage(
                      {
                        id: 'learn.captions_transcript.jump_to_time',
                        defaultMessage: '{text} Jump to {time}',
                      },
                      { time: entry.startLabel, text: entry.text },
                    )}
                    cn={cn(
                      'shrink-0 !h-auto !p-0 text-sm font-bold underline outline-none focus-visible:ring-2 focus-visible:ring-blue-600',
                      textBlue,
                    )}
                  >
                    {entry.startLabel}
                  </Button>
                  <Prose
                    data-testid="captions-entry-content"
                    content={html}
                    className={cn(
                      'flex-1 min-w-0 text-lg md:text-base font-bold md:font-normal',
                      '[&_a]:text-blue-600 [&_a]:underline dark:[&_a]:text-blue-400',
                      text5,
                    )}
                  />
                  <Box className="absolute right-2 top-1/2 z-10 hidden -translate-y-1/2 group-hover:flex group-focus-within:flex">
                    <OsmosisAIButton
                      handleClickOverride={() => {
                        onAskAI(entry.text);
                      }}
                      leftIcon={
                        <Icon
                          icon={faWandMagicSparkles}
                          className="mr-1"
                          size={12}
                        />
                      }
                      classNames={{
                        container: cn('w-32'),
                        button: cn(
                          bg0,
                          borderColorBlue600,
                          `hover:!text-white w-32 h-[26px] rounded-full py-0.5 px-2.5 !text-blue-600 ![background-image:none] duration-200 hover:bg-blue-600 hover:!text-white hover:animate-none`,
                          '',
                        ),
                        text: cn('!text-xs !font-bold !text-inherit'),
                      }}
                      skipShowTracking
                      showLoggedOut
                      persistButtonVisible
                    />
                  </Box>
                </Flex>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
}
